import { useQueryClient } from '@tanstack/react-query';
import { useShallow } from 'zustand/react/shallow';
import { toast } from 'react-toastify';
import { useConfigurationStore } from '@store/configurationStore';
import { useShoppingCart } from '@hooks/useShoppingCart/useShoppingCart';
import type { Plan } from '@t/productOffering.types';
import type { ProductOfferingDTO } from '@t/api.types';

export const useConfigurePlanActions = () => {
  const { activePlanConfig, selectedEquipment, selectedAddOns, saveAndClose, transitionToAddOns } = useConfigurationStore(
    useShallow((s) => ({
      activePlanConfig: s.activePlanConfig,
      selectedEquipment: s.selectedEquipment,
      selectedAddOns: s.selectedAddOns,
      saveAndClose: s.saveAndClose,
      transitionToAddOns: s.transitionToAddOns,
    }))
  );

  const sessionId = activePlanConfig?.sessionId ?? '';
  const homeId = activePlanConfig?.homeIdOfQualifiedOffers ?? '';
  const planId = activePlanConfig?.planId ?? '';
  const parentOfferingCode = activePlanConfig?.parentOfferingCode ?? '';

  const queryClient = useQueryClient();
  const { addTopOfferAsync, selectAddOnsAsync, isPending } = useShoppingCart(sessionId);

  const hasAddOns = activePlanConfig?.hasAddOns ?? false;

  const confirmHardware = async () => {
    try {
      // When plan addition was deferred (user had to confirm equipment first),
      // add the plan to the cart before adding equipment children.
      if (activePlanConfig?.pendingPlanAddition) {
        await addTopOfferAsync({
          action: 'add',
          quantity: 1,
          productOffering: { id: planId, name: activePlanConfig.planName },
        });
      }

      if (selectedEquipment) {
        const equipmentDTOs = queryClient.getQueryData<ProductOfferingDTO[]>(['equipment', sessionId, homeId, planId]);
        const dto = equipmentDTOs?.find((e) => e.id === selectedEquipment.id);
        if (dto) {
          await selectAddOnsAsync(parentOfferingCode, [
            {
              id: dto.id ?? selectedEquipment.id,
              name: dto.name ?? 'Equipment',
              quantity: selectedEquipment.quantity,
            },
          ]);
        }
      }

      toast.success('The plan and equipment were successfully added to the cart');

      if (hasAddOns) {
        transitionToAddOns();
      } else {
        saveAndClose();
      }
    } catch {
      toast.error('Something went wrong while adding the plan to your cart. Please try again.');
    }
  };

  const addSelectedAddOnsToPlan = async () => {
    try {
      const plans = queryClient.getQueryData<Plan[]>(['productOfferings', sessionId, homeId]);
      const plan = plans?.find((p) => p.planId === planId);
      const itemsToAdd = selectedAddOns
        .map((selected) => {
          const addOn = plan?.addOns?.find((a) => a.code === selected.code);
          return addOn ? { id: addOn.id, name: addOn.name, quantity: selected.quantity } : null;
        })
        .filter((x): x is { id: string; name: string; quantity: number } => x !== null);

      if (itemsToAdd.length > 0) {
        await selectAddOnsAsync(parentOfferingCode, itemsToAdd);
      }

      toast.success('The add-ons were successfully added to the plan in your cart');
      saveAndClose();
    } catch {
      toast.error('Something went wrong while adding add-ons to your plan. Please try again.');
    }
  };

  const skipAddOns = () => {
    saveAndClose();
  };

  return { confirmHardware, addSelectedAddOnsToPlan, skipAddOns, isPending };
};
